import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  appendOrganizationBrandIds,
  appendOrganizationClientIds,
  organizationClientIdsKey,
} from "../../lib/utils/organizationReportClientIds"

function conversationTopicsKey({ organizationId, startDate, endDate, clientIds, brandIds } = {}) {
  return `${String(organizationId ?? "")}|${String(startDate ?? "")}|${String(endDate ?? "")}|${organizationClientIdsKey(clientIds)}|${organizationClientIdsKey(brandIds)}`
}

/**
 * GET /api/v1/report/organizations/{organizationId}/messages/conversation-topics?startDate=&endDate=&client_ids=&brand_ids=
 */
export const fetchOrganizationConversationTopics = createAsyncThunk(
  "organizationConversationTopics/fetch",
  async ({ organizationId, startDate, endDate, clientIds, brandIds } = {}, { rejectWithValue }) => {
    try {
      if (organizationId == null || organizationId === "") {
        return rejectWithValue("organizationId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const query = new URLSearchParams({
        startDate,
        endDate,
      })
      appendOrganizationClientIds(query, clientIds)
      appendOrganizationBrandIds(query, brandIds)
      const data = await get(
        `report/organizations/${organizationId}/messages/conversation-topics?${query}`,
      )

      return {
        data,
        organizationId,
        startDate,
        endDate,
        fetchKey: conversationTopicsKey({ organizationId, startDate, endDate, clientIds, brandIds }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch organization conversation topics")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = conversationTopicsKey(arg)
      const state = getState().organizationConversationTopics
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  data: null,
  status: "idle",
  error: null,
  organizationId: null,
  startDate: null,
  endDate: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
}

const organizationConversationTopicsSlice = createSlice({
  name: "organizationConversationTopics",
  initialState,
  reducers: {
    clearOrganizationConversationTopics: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationConversationTopics.pending, (state, action) => {
        const arg = action.meta.arg || {}
        state.status = "loading"
        state.error = null
        state.requestedFetchKey = conversationTopicsKey(arg)
      })
      .addCase(fetchOrganizationConversationTopics.fulfilled, (state, action) => {
        if (action.payload?.fetchKey !== state.requestedFetchKey) return
        state.status = "succeeded"
        state.data = action.payload?.data ?? null
        state.organizationId = action.payload?.organizationId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchOrganizationConversationTopics.rejected, (state, action) => {
        if (conversationTopicsKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")) {
          return
        }
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch organization conversation topics"
        state.data = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearOrganizationConversationTopics } = organizationConversationTopicsSlice.actions
export default organizationConversationTopicsSlice.reducer
